import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { useAddRecordField, useRecordConfig, useUpdateRecordField, useUpdateRecordLabels } from './recordHooks';
import {
  PRIMARY_RECORD_FIELD_TYPES,
  RECORD_FIELD_TYPES,
  type RecordFieldDataType,
  type RecordFieldDef
} from './recordTypes';

/**
 * Settings section to configure the per-tenant customer-record type: the label
 * ("Pets"/"Houses") and its fields. A pet sitter leaves it as-is; a house sitter
 * relabels and swaps the fields.
 */
export function RecordConfigSettings() {
  const { data: config } = useRecordConfig(true);
  const updateLabels = useUpdateRecordLabels();
  const addField = useAddRecordField();
  const updateField = useUpdateRecordField();

  const [singular, setSingular] = useState('');
  const [plural, setPlural] = useState('');
  const [labelStatus, setLabelStatus] = useState<'' | 'saved'>('');

  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState<RecordFieldDataType>('Text');
  const [newRequired, setNewRequired] = useState(false);

  useEffect(() => {
    if (config) {
      setSingular(config.singularLabel);
      setPlural(config.pluralLabel);
    }
  }, [config]);

  if (!config) return null;

  const fields = config.fields.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const labelsDirty = singular.trim() !== config.singularLabel || plural.trim() !== config.pluralLabel;

  const saveLabels = async () => {
    if (!singular.trim() || !plural.trim()) return;
    await updateLabels.mutateAsync({ singularLabel: singular.trim(), pluralLabel: plural.trim() });
    setLabelStatus('saved');
    setTimeout(() => setLabelStatus(''), 2000);
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= fields.length) return;
    const a = fields[index];
    const b = fields[target];
    await updateField.mutateAsync({ id: a.id, sortOrder: b.sortOrder });
    await updateField.mutateAsync({ id: b.id, sortOrder: a.sortOrder });
  };

  const onAdd = async () => {
    if (!newLabel.trim()) return;
    await addField.mutateAsync({ label: newLabel.trim(), dataType: newType, isRequired: newRequired });
    setNewLabel('');
    setNewType('Text');
    setNewRequired(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-800">Client records</h2>
        <p className="text-sm text-slate-500 mt-1">
          Configure the sub-record kept on each customer — the label and its fields. For example, a
          pet sitter uses “Pets”; a house sitter could use “Houses”.
        </p>
      </div>

      {/* Labels */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-slate-600 flex flex-col gap-1">
          Singular label
          <input
            value={singular}
            onChange={(e) => setSingular(e.target.value)}
            className="border border-slate-300 rounded px-3 py-2 min-h-[40px]"
          />
        </label>
        <label className="text-sm text-slate-600 flex flex-col gap-1">
          Plural label
          <input
            value={plural}
            onChange={(e) => setPlural(e.target.value)}
            className="border border-slate-300 rounded px-3 py-2 min-h-[40px]"
          />
        </label>
        <button
          type="button"
          onClick={saveLabels}
          disabled={!labelsDirty || updateLabels.isPending}
          className="bg-sky-600 text-white px-3 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
        >
          Save labels
        </button>
        {labelStatus === 'saved' && <span className="text-green-600 text-sm">Saved.</span>}
      </div>

      {/* Fields */}
      <div>
        <h3 className="text-sm font-medium text-slate-700 mb-2">Fields</h3>
        <ul className="divide-y divide-slate-100">
          {fields.map((f, i) => (
            <FieldRow
              key={f.id}
              field={f}
              canMoveUp={i > 0}
              canMoveDown={i < fields.length - 1}
              busy={updateField.isPending}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, 1)}
              onChange={(patch) => updateField.mutate({ id: f.id, ...patch })}
            />
          ))}
        </ul>
      </div>

      {/* Add field */}
      <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
        <label className="text-sm text-slate-600 flex flex-col gap-1 flex-1 min-w-[160px]">
          New field
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Field label…"
            className="border border-slate-300 rounded px-3 py-2 min-h-[40px]"
          />
        </label>
        <label className="text-sm text-slate-600 flex flex-col gap-1">
          Type
          <select
            value={newType}
            onChange={(e) => setNewType(e.target.value as RecordFieldDataType)}
            className="border border-slate-300 rounded px-3 py-2 min-h-[40px]"
          >
            {RECORD_FIELD_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-slate-600 flex items-center gap-1 h-[40px]">
          <input type="checkbox" checked={newRequired} onChange={(e) => setNewRequired(e.target.checked)} />
          Required
        </label>
        <button
          type="button"
          onClick={onAdd}
          disabled={!newLabel.trim() || addField.isPending}
          className="inline-flex items-center gap-1 bg-sky-600 text-white px-3 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm h-[40px]"
        >
          <Plus size={14} /> Add
        </button>
      </div>
    </div>
  );
}

function FieldRow({
  field,
  canMoveUp,
  canMoveDown,
  busy,
  onMoveUp,
  onMoveDown,
  onChange
}: {
  field: RecordFieldDef;
  canMoveUp: boolean;
  canMoveDown: boolean;
  busy: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onChange: (patch: { label?: string; dataType?: RecordFieldDataType; isRequired?: boolean; isActive?: boolean }) => void;
}) {
  const [label, setLabel] = useState(field.label);
  const dirty = label.trim() !== field.label && label.trim().length > 0;

  return (
    <li className={`flex flex-wrap items-center gap-2 py-2 ${field.isActive ? '' : 'opacity-50'}`}>
      <div className="flex flex-col">
        <button type="button" onClick={onMoveUp} disabled={!canMoveUp || busy}
          className="text-slate-400 hover:text-slate-700 disabled:opacity-30" aria-label="Move up">
          <ChevronUp size={14} />
        </button>
        <button type="button" onClick={onMoveDown} disabled={!canMoveDown || busy}
          className="text-slate-400 hover:text-slate-700 disabled:opacity-30" aria-label="Move down">
          <ChevronDown size={14} />
        </button>
      </div>

      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        className="flex-1 min-w-[140px] border border-slate-300 rounded px-3 py-2 min-h-[38px]"
      />

      {field.isPrimaryName && (
        <span className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-0.5">
          Primary
        </span>
      )}
      <select
        value={field.dataType}
        onChange={(e) => onChange({ dataType: e.target.value as RecordFieldDataType })}
        className="border border-slate-300 rounded px-2 py-2 text-sm min-h-[38px]"
        title={field.isPrimaryName ? 'The primary field must be a text type' : undefined}
      >
        {(field.isPrimaryName ? PRIMARY_RECORD_FIELD_TYPES : RECORD_FIELD_TYPES).map((t) => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>

      <label className="text-xs text-slate-600 flex items-center gap-1">
        <input
          type="checkbox"
          checked={field.isPrimaryName ? true : field.isRequired}
          disabled={field.isPrimaryName}
          onChange={(e) => onChange({ isRequired: e.target.checked })}
        />
        Required
      </label>

      {dirty && (
        <button
          type="button"
          onClick={() => onChange({ label: label.trim() })}
          className="text-sm px-2 py-1 rounded border border-sky-300 bg-sky-50 text-sky-700"
        >
          Save
        </button>
      )}

      {!field.isPrimaryName && (
        <button
          type="button"
          onClick={() => onChange({ isActive: !field.isActive })}
          className={`text-xs px-2 py-1 rounded border ${
            field.isActive
              ? 'border-slate-300 bg-slate-50 text-slate-600'
              : 'border-sky-300 bg-sky-50 text-sky-700'
          }`}
        >
          {field.isActive ? 'Deactivate' : 'Reactivate'}
        </button>
      )}
    </li>
  );
}
